package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.*
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.Element

private const val SVG_NS = "http://www.w3.org/2000/svg"

/**
 * SVG connector lines between matches in the bracket tree.
 *
 * Draws traditional bracket-style connectors with shared vertical junctions:
 *   [Top Match] ────┐
 *                   ├──── [Next Match]
 *   [Bot Match] ────┘
 *
 * Uses raw DOM manipulation to properly create SVG elements in the SVG namespace.
 */
@Composable
fun BracketConnectorSvg(
    connectors: List<ConnectorLine>,
    totalWidth: Double,
    totalHeight: Double,
    highlightedMatchId: String? = null
) {
    // Use a placeholder div and create SVG via DOM manipulation
    Div({
        attr("data-connector-container", "true")
        style {
            property("position", "absolute")
            property("top", "0")
            property("left", "0")
            property("width", "${totalWidth.toInt()}px")
            property("height", "${totalHeight.toInt()}px")
            property("pointer-events", "none")
        }
    }) {
        DisposableEffect(connectors, totalWidth, totalHeight, highlightedMatchId) {
            val container = scopeElement

            // Clear any existing SVG
            while (container.firstChild != null) {
                container.removeChild(container.firstChild!!)
            }

            // Create SVG element with proper namespace
            val svg = document.createElementNS(SVG_NS, "svg").apply {
                setAttribute("width", "${totalWidth.toInt()}")
                setAttribute("height", "${totalHeight.toInt()}")
                setAttribute("viewBox", "0 0 ${totalWidth.toInt()} ${totalHeight.toInt()}")
                setAttribute("style", "position: absolute; top: 0; left: 0; overflow: visible;")
            }

            // Group connectors by target match to draw bracket-style connections
            val connectorsByTarget = connectors.groupBy { it.toMatchId }

            connectorsByTarget.forEach { (_, pairConnectors) ->
                val isHighlighted = pairConnectors.any {
                    it.fromMatchId == highlightedMatchId || it.toMatchId == highlightedMatchId
                }

                val strokeColor = if (isHighlighted) "#00A651" else "#AAAAAA"
                val strokeWidth = if (isHighlighted) "3" else "2"

                // Check if connectors come from different halves (SF→Final case)
                val fromDifferentHalves = pairConnectors.size == 2 &&
                    pairConnectors[0].isLeftHalf != pairConnectors[1].isLeftHalf

                if (fromDifferentHalves) {
                    // Special case: Semifinals to Final - draw each connector separately
                    // since they come from opposite sides of the bracket
                    pairConnectors.forEach { connector ->
                        drawSingleConnector(
                            svg = svg,
                            connector = connector,
                            strokeColor = strokeColor,
                            strokeWidth = strokeWidth
                        )
                    }
                } else if (pairConnectors.size == 2) {
                    // Draw bracket-style connector (two sources from same half, one target)
                    val topConnector = pairConnectors.minByOrNull { it.fromY }!!
                    val bottomConnector = pairConnectors.maxByOrNull { it.fromY }!!

                    drawBracketConnector(
                        svg = svg,
                        topConnector = topConnector,
                        bottomConnector = bottomConnector,
                        strokeColor = strokeColor,
                        strokeWidth = strokeWidth
                    )
                } else if (pairConnectors.size == 1) {
                    // Single connector
                    val connector = pairConnectors[0]
                    drawSingleConnector(
                        svg = svg,
                        connector = connector,
                        strokeColor = strokeColor,
                        strokeWidth = strokeWidth
                    )
                }
            }

            container.appendChild(svg)

            onDispose {
                // Cleanup handled by Compose
            }
        }
    }
}

/**
 * Draws a traditional bracket-style connector where two matches
 * connect via a shared vertical line to the next round.
 *
 * Left half visual:          Right half visual:
 *   [Top Match] ────┐              ┌──── [Top Match]
 *                   ├── [Next]  [Next] ──┤
 *   [Bot Match] ────┘              └──── [Bot Match]
 */
private fun drawBracketConnector(
    svg: Element,
    topConnector: ConnectorLine,
    bottomConnector: ConnectorLine,
    strokeColor: String,
    strokeWidth: String
) {
    val topFromX = topConnector.fromX
    val topFromY = topConnector.fromY
    val bottomFromX = bottomConnector.fromX
    val bottomFromY = bottomConnector.fromY
    val toX = topConnector.toX
    val toY = topConnector.toY

    // Junction X: midpoint between source matches and target
    val junctionX = (topFromX + toX) / 2

    // Junction center Y: midpoint between the two source matches
    val junctionCenterY = (topFromY + bottomFromY) / 2

    // Draw 4 line segments using a single path for efficiency
    val pathData = buildString {
        // 1. Top match → junction (horizontal)
        append("M $topFromX $topFromY L $junctionX $topFromY ")
        // 2. Bottom match → junction (horizontal)
        append("M $bottomFromX $bottomFromY L $junctionX $bottomFromY ")
        // 3. Vertical connector between top and bottom
        append("M $junctionX $topFromY L $junctionX $bottomFromY ")
        // 4. Junction center → next match (horizontal)
        append("M $junctionX $junctionCenterY L $toX $toY")
    }

    val path = document.createElementNS(SVG_NS, "path").apply {
        setAttribute("d", pathData)
        setAttribute("fill", "none")
        setAttribute("stroke", strokeColor)
        setAttribute("stroke-width", strokeWidth)
        setAttribute("stroke-linecap", "round")
    }

    svg.appendChild(path)
}

/**
 * Draws a single connector line (used for edge cases).
 */
private fun drawSingleConnector(
    svg: Element,
    connector: ConnectorLine,
    strokeColor: String,
    strokeWidth: String
) {
    val x1 = connector.fromX
    val y1 = connector.fromY
    val x2 = connector.toX
    val y2 = connector.toY

    // Midpoint for step connector
    val midX = (x1 + x2) / 2
    val midY = (y1 + y2) / 2

    // Draw step connector: horizontal to mid, vertical, horizontal to end
    val pathData = "M $x1 $y1 L $midX $y1 L $midX $y2 L $x2 $y2"

    val path = document.createElementNS(SVG_NS, "path").apply {
        setAttribute("d", pathData)
        setAttribute("fill", "none")
        setAttribute("stroke", strokeColor)
        setAttribute("stroke-width", strokeWidth)
        setAttribute("stroke-linecap", "round")
        setAttribute("stroke-linejoin", "round")
    }

    svg.appendChild(path)
}

/**
 * Simple straight connector line (alternative to curved).
 */
@Composable
fun StraightConnectorLine(
    connector: ConnectorLine,
    isHighlighted: Boolean
) {
    val strokeColor = if (isHighlighted) "#00A651" else "#E0E0E0"
    val strokeWidth = if (isHighlighted) "3" else "2"

    Div({
        style {
            property("position", "absolute")
            property("pointer-events", "none")
        }
    }) {
        DisposableEffect(connector, isHighlighted) {
            val container = scopeElement

            val x1 = connector.fromX
            val y1 = connector.fromY
            val x2 = connector.toX
            val y2 = connector.toY
            val midX = (x1 + x2) / 2

            val svg = document.createElementNS(SVG_NS, "svg").apply {
                setAttribute("style", "position: absolute; top: 0; left: 0; overflow: visible;")
            }

            val path = document.createElementNS(SVG_NS, "path").apply {
                setAttribute("d", "M $x1 $y1 L $midX $y1 L $midX $y2 L $x2 $y2")
                setAttribute("fill", "none")
                setAttribute("stroke", strokeColor)
                setAttribute("stroke-width", strokeWidth)
                setAttribute("stroke-linecap", "round")
                setAttribute("stroke-linejoin", "round")
            }

            svg.appendChild(path)
            container.appendChild(svg)

            onDispose { }
        }
    }
}

/**
 * Animated connector that shows the "flow" of a pick to the next round.
 */
@Composable
fun AnimatedConnectorSvg(
    fromX: Double,
    fromY: Double,
    toX: Double,
    toY: Double,
    isLeftHalf: Boolean,
    onAnimationComplete: () -> Unit
) {
    LaunchedEffect(Unit) {
        // Animation would be handled via CSS or JS interop
        // For now, just mark complete immediately
        onAnimationComplete()
    }

    Div({
        style {
            property("position", "absolute")
            property("top", "0")
            property("left", "0")
            property("width", "100%")
            property("height", "100%")
            property("pointer-events", "none")
            property("z-index", "10")
        }
    }) {
        DisposableEffect(fromX, fromY, toX, toY, isLeftHalf) {
            val container = scopeElement
            val midX = (fromX + toX) / 2
            val curveRadius = BracketLayoutConfig.CONNECTOR_CURVE_RADIUS.toDouble()

            val svg = document.createElementNS(SVG_NS, "svg").apply {
                setAttribute("style", "position: absolute; top: 0; left: 0; width: 100%; height: 100%; overflow: visible;")
            }

            val pathData = if (isLeftHalf) {
                buildString {
                    append("M $fromX $fromY ")
                    append("L ${midX - curveRadius} $fromY ")
                    append("Q $midX $fromY $midX ${if (toY > fromY) fromY + curveRadius else fromY - curveRadius} ")
                    append("L $midX ${if (toY > fromY) toY - curveRadius else toY + curveRadius} ")
                    append("Q $midX $toY ${midX + curveRadius} $toY ")
                    append("L $toX $toY")
                }
            } else {
                buildString {
                    append("M $fromX $fromY ")
                    append("L ${midX + curveRadius} $fromY ")
                    append("Q $midX $fromY $midX ${if (toY > fromY) fromY + curveRadius else fromY - curveRadius} ")
                    append("L $midX ${if (toY > fromY) toY - curveRadius else toY + curveRadius} ")
                    append("Q $midX $toY ${midX - curveRadius} $toY ")
                    append("L $toX $toY")
                }
            }

            val path = document.createElementNS(SVG_NS, "path").apply {
                setAttribute("d", pathData)
                setAttribute("fill", "none")
                setAttribute("stroke", "#00A651")
                setAttribute("stroke-width", "3")
                setAttribute("stroke-linecap", "round")
                setAttribute("class", "connector-animated")
            }

            val circle = document.createElementNS(SVG_NS, "circle").apply {
                setAttribute("cx", "${toX.toInt()}")
                setAttribute("cy", "${toY.toInt()}")
                setAttribute("r", "6")
                setAttribute("fill", "#00A651")
                setAttribute("class", "connector-dot-animated")
            }

            svg.appendChild(path)
            svg.appendChild(circle)
            container.appendChild(svg)

            onDispose { }
        }
    }
}
